import { sanityOriginClient } from './client';
import {
  articlePageBySlugQuery,
  articlePageDraftBySlugQuery
} from './queries';
import { buildTeamRevealPath } from './teamReveal';
import type { SanityArticlePage } from './types';
import type { ContentDocumentSelector } from '@api-service/content';

/**
 * Sanity articles live under their own prefix rather than the site root the
 * WordPress catch-all owns (FFH-2630). One prefix per content type also lets
 * the mobile app route universal links by path alone.
 */
export const ARTICLES_PATH_PREFIX = '/articles';

export const buildArticlePath = (slug: string): string =>
  `${ARTICLES_PATH_PREFIX}/${slug}`;

/**
 * The canonical path of either editorial document type. A reveal without an
 * author cannot exist (the schema requires one), so the fallback only guards
 * the type.
 */
export const buildArticleDocumentPath = (page: SanityArticlePage): string =>
  page._type === 'teamReveal' && page.author
    ? buildTeamRevealPath(page.author.slug, page.slug.current)
    : buildArticlePath(page.slug.current);

/**
 * How the content API addresses the same document: a reveal by its author's
 * handle and slug, an article by slug alone.
 */
export const buildContentDocumentSelector = (
  page: SanityArticlePage
): ContentDocumentSelector =>
  page._type === 'teamReveal' && page.author
    ? {
        type: 'teamReveal',
        authorSlug: page.author.slug,
        slug: page.slug.current
      }
    : { type: 'articlePage', slug: page.slug.current };

export const getAllArticlePageSlugsQuery = `
  *[_type == "articlePage" && defined(slug.current)] { "slug": slug.current }
`;

export const getArticlePage = async (
  slug: string
): Promise<SanityArticlePage | null> => {
  const page = await sanityOriginClient.fetch<SanityArticlePage | null>(
    articlePageBySlugQuery,
    { slug }
  );
  return page ?? null;
};

/**
 * Editors check an article before publishing, so this reads the draft when one
 * exists and the published document otherwise, premium body included. Only
 * the preview route calls it.
 */
export const getArticlePageDraft = async (
  slug: string
): Promise<SanityArticlePage | null> => {
  const page = await sanityOriginClient
    .withConfig({ perspective: 'drafts' })
    .fetch<SanityArticlePage | null>(articlePageDraftBySlugQuery, { slug });
  return page ?? null;
};
